/**
 * CartView — Shipping-Aware Multi-Seller Deck Cart Optimizer
 *
 * Powered by `@playtable/cart-optimizer`:
 * - Optimizes cards + shipping thresholds jointly across `Cheapest Total`, `Balanced`, and `Fewest Packages`
 * - Automatically subtracts owned cards from Deckstr's `deckstr_collection` binder + optional pasted/CSV list + basic lands
 * - Anchors seller quotes to live Scryfall `/cards/collection` market prices
 * - Provides per-package mass-entry copy, affiliate checkout links, and 1-click Out-of-Stock re-optimization
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Layers,
  Loader2,
  Package,
  RefreshCw,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  TrendingDown,
  Truck,
} from 'lucide-react';
import {
  BASIC_LAND_NAMES,
  VENDOR_SHIPPING_SCHEDULES,
  buildSimulatedStockCheckUpdates,
  normalizeCardKey,
  optimizeDeckCart,
  reoptimizeAfterStockCheck,
  seedVendorQuotesForItems,
  type CardCondition as OptimizerCondition,
  type CartDeckItem,
  type FinishPreference,
  type OptimizationMode,
  type PrintingPreference,
  type ReoptimizedCartOutcome,
  type StockVerificationUpdate,
  type VendorId,
  type VendorQuote,
} from '#cart-optimizer';
import type { Card } from '../../App';
import {
  buildCardKingdomUrl,
  buildCardmarketUrl,
  buildTCGPlayerCartUrl,
  formatPrice,
  type CollectionCard,
} from './types';

interface CartViewProps {
  deck: Card[];
  sideboard?: Card[];
  deckName?: string;
  currency: 'usd' | 'eur';
}

interface ScryfallAnchorPrice {
  usd: number | null;
  usdFoil: number | null;
  eur: number | null;
  setCode?: string;
}

const ALL_VENDOR_IDS: VendorId[] = [
  'tcgplayer_direct',
  'tcgplayer_market',
  'card_kingdom',
  'coolstuffinc',
  'starcitygames',
  'cardmarket',
];

function readDeckstrBinderCounts(): { totalBinderCards: number; counts: Record<string, number> } {
  try {
    const raw = localStorage.getItem('deckstr_collection');
    if (!raw) return { totalBinderCards: 0, counts: {} };
    const parsed = JSON.parse(raw) as CollectionCard[];
    if (!Array.isArray(parsed)) return { totalBinderCards: 0, counts: {} };
    const counts: Record<string, number> = {};
    let total = 0;
    for (const entry of parsed) {
      if (!entry?.cardName || !entry.quantity || entry.quantity <= 0) continue;
      const key = normalizeCardKey(entry.cardName);
      counts[key] = (counts[key] ?? 0) + entry.quantity;
      total += entry.quantity;
    }
    return { totalBinderCards: total, counts };
  } catch {
    return { totalBinderCards: 0, counts: {} };
  }
}

export function CartView({
  deck,
  sideboard = [],
  deckName = 'Untitled Deck',
  currency,
}: CartViewProps) {
  const [mode, setMode] = useState<OptimizationMode>('cheapest_total');
  const [minCondition, setMinCondition] = useState<OptimizerCondition>('LP');
  const [finishPreference, setFinishPreference] = useState<FinishPreference>('nonfoil');
  const [printingPreference, setPrintingPreference] = useState<PrintingPreference>('cheapest_printing');
  const [excludeBasicLands, setExcludeBasicLands] = useState(true);
  const [subtractBinder, setSubtractBinder] = useState(true);
  const [extraOwnedText, setExtraOwnedText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOwnedBox, setShowOwnedBox] = useState(false);

  const [allowedVendors, setAllowedVendors] = useState<VendorId[]>(() =>
    currency === 'eur'
      ? ['cardmarket', 'tcgplayer_direct', 'tcgplayer_market', 'card_kingdom']
      : ['tcgplayer_direct', 'tcgplayer_market', 'card_kingdom', 'coolstuffinc', 'starcitygames']
  );

  const [anchorPrices, setAnchorPrices] = useState<Record<string, ScryfallAnchorPrice>>({});
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [stockUpdates, setStockUpdates] = useState<StockVerificationUpdate[]>([]);
  const [lastReoptReport, setLastReoptReport] = useState<ReoptimizedCartOutcome | null>(null);
  const [copiedPkgId, setCopiedPkgId] = useState<string | null>(null);
  const [expandedChecklists, setExpandedChecklists] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (currency === 'eur' && !allowedVendors.includes('cardmarket')) {
      setAllowedVendors((prev) => ['cardmarket', ...prev]);
    }
  }, [currency, allowedVendors]);

  // Consolidate deck + sideboard into CartDeckItem[]
  const deckItems = useMemo<CartDeckItem[]>(() => {
    const map = new Map<string, CartDeckItem>();
    const combined = [...deck, ...sideboard].filter((c) => !c.isExtraCard && c.quantity > 0);
    for (const c of combined) {
      const key = normalizeCardKey(c.name);
      const isBasic =
        BASIC_LAND_NAMES.has(key) ||
        c.rarity === 'B' ||
        (c.type ?? '').toLowerCase().includes('basic land');
      const existing = map.get(key);
      if (existing) {
        existing.quantity += c.quantity;
      } else {
        map.set(key, {
          cardName: c.name,
          quantity: c.quantity,
          setCode: (c as Card & { setCode?: string }).setCode,
          foil: false,
          isBasicLand: isBasic,
        });
      }
    }
    return Array.from(map.values());
  }, [deck, sideboard]);

  const binderInfo = useMemo(() => readDeckstrBinderCounts(), [deckItems]);

  // Batch-fetch live Scryfall market anchors via `/cards/collection` (up to 75 per call)
  const refreshLiveAnchors = useCallback(async () => {
    if (deckItems.length === 0) return;
    setIsPricingLoading(true);
    const nextAnchors: Record<string, ScryfallAnchorPrice> = {};

    try {
      const nonBasics = deckItems.filter((d) => !d.isBasicLand);
      for (let i = 0; i < nonBasics.length; i += 75) {
        const chunk = nonBasics.slice(i, i + 75);
        const identifiers = chunk.map((item) => ({
          name: item.cardName.includes(' // ') ? item.cardName.split(' // ')[0].trim() : item.cardName,
        }));
        const res = await fetch('https://api.scryfall.com/cards/collection', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifiers }),
        });
        if (!res.ok) continue;
        const payload = await res.json();
        if (Array.isArray(payload?.data)) {
          for (const cardObj of payload.data) {
            const key = normalizeCardKey(cardObj.name ?? '');
            if (!key) continue;
            nextAnchors[key] = {
              usd: cardObj.prices?.usd ? parseFloat(cardObj.prices.usd) : null,
              usdFoil: cardObj.prices?.usd_foil ? parseFloat(cardObj.prices.usd_foil) : null,
              eur: cardObj.prices?.eur ? parseFloat(cardObj.prices.eur) : null,
              setCode: typeof cardObj.set === 'string' ? cardObj.set.toUpperCase() : undefined,
            };
          }
        }
      }
      setAnchorPrices(nextAnchors);
      setStockUpdates([]);
      setLastReoptReport(null);
    } catch {
      // Offline / rate-limited fallback uses deterministic seeded vendor quotes cleanly
    } finally {
      setIsPricingLoading(false);
    }
  }, [deckItems]);

  useEffect(() => {
    if (deckItems.length > 0 && Object.keys(anchorPrices).length === 0) {
      void refreshLiveAnchors();
    }
  }, [deckItems.length, anchorPrices, refreshLiveAnchors]);

  // Build multi-seller quote pool anchored to live Scryfall prices when available
  const baseQuotes = useMemo<VendorQuote[]>(() => {
    const seeded = seedVendorQuotesForItems(deckItems);
    if (Object.keys(anchorPrices).length === 0) return seeded;

    // Compute per-card seeded median so we scale seller spreads realistically around real Scryfall USD/EUR!
    const seededBaseByCard = new Map<string, number>();
    for (const q of seeded) {
      if (q.vendorId === 'tcgplayer_market' && !q.foil && q.condition === 'NM') {
        const key = normalizeCardKey(q.cardName);
        if (!seededBaseByCard.has(key)) {
          seededBaseByCard.set(key, q.unitPriceUsd);
        }
      }
    }

    return seeded.map((q) => {
      const key = normalizeCardKey(q.cardName);
      const anchor = anchorPrices[key];
      const seededRef = seededBaseByCard.get(key);
      if (!anchor || !seededRef || seededRef <= 0) return q;

      const targetAnchor =
        q.foil && anchor.usdFoil
          ? anchor.usdFoil
          : q.vendorId === 'cardmarket' && anchor.eur
          ? anchor.eur * 1.08
          : anchor.usd;

      if (!targetAnchor || targetAnchor <= 0) return q;

      const ratio = q.unitPriceUsd / (q.foil ? seededRef * 1.65 : seededRef);
      const scaled = Math.max(0.08, Math.round(targetAnchor * ratio * 100) / 100);
      return {
        ...q,
        unitPriceUsd: scaled,
      };
    });
  }, [deckItems, anchorPrices]);

  // Apply any live stock-check / sold-out updates
  const activeQuotes = useMemo<VendorQuote[]>(() => {
    if (stockUpdates.length === 0) return baseQuotes;
    const updMap = new Map(stockUpdates.map((u) => [u.quoteId, u]));
    return baseQuotes.map((q) => {
      const u = updMap.get(q.quoteId);
      if (!u) return q;
      return {
        ...q,
        stockQty: Math.max(0, u.availableQty),
        unitPriceUsd: u.updatedUnitPriceUsd ?? q.unitPriceUsd,
      };
    });
  }, [baseQuotes, stockUpdates]);

  const preferences = useMemo(
    () => ({
      mode,
      minCondition,
      finishPreference,
      printingPreference,
      excludeBasicLands,
      allowedVendors: allowedVendors.length > 0 ? allowedVendors : ALL_VENDOR_IDS,
    }),
    [mode, minCondition, finishPreference, printingPreference, excludeBasicLands, allowedVendors]
  );

  const optimized = useMemo(() => {
    return optimizeDeckCart({
      items: deckItems,
      quotes: activeQuotes,
      ownedCollectionCounts: subtractBinder ? binderInfo.counts : undefined,
      ownedCollectionText: extraOwnedText.trim() || undefined,
      preferences,
    });
  }, [deckItems, activeQuotes, subtractBinder, binderInfo.counts, extraOwnedText, preferences]);

  const fxMult = currency === 'eur' ? 0.92 : 1.0;
  const fmtMoney = (usdVal: number) => formatPrice(usdVal * fxMult, currency);

  const toggleVendor = (vid: VendorId) => {
    setAllowedVendors((prev) => {
      if (prev.includes(vid)) {
        return prev.length > 1 ? prev.filter((v) => v !== vid) : prev;
      }
      return [...prev, vid];
    });
  };

  const handleSimulateStockVerify = () => {
    const simulated = buildSimulatedStockCheckUpdates(optimized);
    if (simulated.length === 0) return;
    const nextUpdates = [...stockUpdates, ...simulated];
    const outcome = reoptimizeAfterStockCheck({
      items: deckItems,
      quotes: baseQuotes,
      previousResult: optimized,
      stockUpdates: nextUpdates,
      ownedCollectionCounts: subtractBinder ? binderInfo.counts : undefined,
      ownedCollectionText: extraOwnedText.trim() || undefined,
      preferences,
    });
    setStockUpdates(nextUpdates);
    setLastReoptReport(outcome);
  };

  const handleMarkLineSoldOut = (quoteId: string) => {
    const nextUpdates = [...stockUpdates, { quoteId, availableQty: 0 }];
    const outcome = reoptimizeAfterStockCheck({
      items: deckItems,
      quotes: baseQuotes,
      previousResult: optimized,
      stockUpdates: nextUpdates,
      ownedCollectionCounts: subtractBinder ? binderInfo.counts : undefined,
      ownedCollectionText: extraOwnedText.trim() || undefined,
      preferences,
    });
    setStockUpdates(nextUpdates);
    setLastReoptReport(outcome);
  };

  const copyPackageList = async (sellerId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPkgId(sellerId);
      setTimeout(() => setCopiedPkgId((cur) => (cur === sellerId ? null : cur)), 1800);
    } catch {
      // ignore clipboard restrictions
    }
  };

  if (deckItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-52 text-white/30">
        <ShoppingCart className="size-12 mb-3 opacity-20" />
        <h3 className="text-sm font-medium text-white/50 mb-1">No Deck Loaded</h3>
        <p className="text-[11px] text-center max-w-sm">
          Import or build a deck in BREW to optimize cards + shipping across stores.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ========== TOP SUMMARY & MODE BAR ========== */}
      <div className="bg-gradient-to-r from-purple-900/30 via-slate-900/60 to-emerald-900/20 border border-purple-500/25 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-500/20 border border-amber-500/35 flex items-center justify-center">
              <ShoppingCart className="size-5 text-amber-400" />
            </div>
            <div>
              <div className="text-[11px] text-white/50 uppercase tracking-wider flex items-center gap-2">
                <span>Shipping-Consolidated Optimizer · {deckName}</span>
                {isPricingLoading && (
                  <span className="inline-flex items-center gap-1 text-amber-300 normal-case">
                    <Loader2 className="size-3 animate-spin" /> Syncing Scryfall...
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2.5">
                <span className="text-2xl font-black text-amber-400 tabular-nums">
                  {fmtMoney(optimized.grandTotalUsd)}
                </span>
                <span className="text-xs text-white/50 tabular-nums">
                  ({fmtMoney(optimized.cardsSubtotalUsd)} cards + {fmtMoney(optimized.shippingTotalUsd)} shipping ·{' '}
                  {optimized.packageCount} {optimized.packageCount === 1 ? 'package' : 'packages'})
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {(
              [
                { id: 'cheapest_total', label: 'Cheapest Total' },
                { id: 'balanced', label: 'Balanced' },
                { id: 'fewest_packages', label: 'Fewest Packages' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setMode(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                  mode === opt.id
                    ? 'bg-amber-500 text-slate-950 border-amber-400 shadow'
                    : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                }`}
              >
                {opt.label}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`px-2.5 py-1.5 rounded-lg text-xs border flex items-center gap-1 ${
                showFilters
                  ? 'bg-purple-500/25 border-purple-400/50 text-purple-200'
                  : 'bg-white/5 border-white/10 text-white/60 hover:text-white'
              }`}
            >
              <SlidersHorizontal className="size-3.5" />
              <span>Preferences</span>
            </button>

            <button
              type="button"
              onClick={() => void refreshLiveAnchors()}
              disabled={isPricingLoading}
              className="px-2.5 py-1.5 rounded-lg text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`size-3.5 ${isPricingLoading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Metrics & Owned Collection Quick Toggles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-1">
          <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/25 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 flex items-center gap-1">
              <TrendingDown className="size-3" /> Saved vs Naive Split
            </div>
            <div className="text-sm font-bold text-emerald-300 tabular-nums">
              {fmtMoney(optimized.savingsVsNaiveUsd)}{' '}
              <span className="text-[10px] font-normal text-emerald-200/70">
                ({optimized.naivePackageCount} → {optimized.packageCount} pkgs)
              </span>
            </div>
          </div>

          <div className="rounded-lg bg-cyan-500/10 border border-cyan-500/25 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-cyan-300 flex items-center gap-1">
              <Layers className="size-3" /> Cards Needed
            </div>
            <div className="text-sm font-bold text-white tabular-nums">
              {optimized.collectionSummary.totalNeededQty} / {optimized.collectionSummary.totalOriginalQty}
              <span className="text-[10px] font-normal text-white/50 ml-1">
                (−{optimized.collectionSummary.excludedBasicsQty} basics, −
                {optimized.collectionSummary.subtractedOwnedQty} owned)
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setSubtractBinder((v) => !v)}
            className={`rounded-lg px-3 py-2 border text-left transition-colors ${
              subtractBinder
                ? 'bg-purple-500/15 border-purple-400/40 text-purple-200'
                : 'bg-white/5 border-white/10 text-white/50'
            }`}
          >
            <div className="text-[10px] uppercase tracking-wider flex items-center justify-between">
              <span>Deckstr Binder</span>
              <span className="font-bold">{subtractBinder ? 'ON' : 'OFF'}</span>
            </div>
            <div className="text-xs font-semibold text-white truncate">
              {binderInfo.totalBinderCards > 0
                ? `${binderInfo.totalBinderCards} binder cards synced`
                : '0 binder cards saved'}
            </div>
          </button>

          <button
            type="button"
            onClick={handleSimulateStockVerify}
            className="rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/40 px-3 py-2 text-left transition-colors"
          >
            <div className="text-[10px] uppercase tracking-wider text-amber-300 flex items-center gap-1">
              <ShieldCheck className="size-3" /> Pre-Checkout Guard
            </div>
            <div className="text-xs font-bold text-amber-100">Verify Stock / Re-Route OOS</div>
          </button>
        </div>

        {/* Expandable Preferences Drawer */}
        {showFilters && (
          <div className="pt-3 border-t border-white/10 space-y-3 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
              <div>
                <label className="block text-[10px] uppercase text-white/50 mb-1">Min Condition</label>
                <select
                  value={minCondition}
                  onChange={(e) => setMinCondition(e.target.value as OptimizerCondition)}
                  className="w-full rounded-lg bg-slate-950 border border-white/15 px-2.5 py-1.5 text-white"
                >
                  <option value="NM">Near Mint (NM)</option>
                  <option value="LP">Lightly Played+ (LP)</option>
                  <option value="MP">Moderately Played+ (MP)</option>
                  <option value="HP">Heavily Played+ (HP)</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase text-white/50 mb-1">Finish</label>
                <select
                  value={finishPreference}
                  onChange={(e) => setFinishPreference(e.target.value as FinishPreference)}
                  className="w-full rounded-lg bg-slate-950 border border-white/15 px-2.5 py-1.5 text-white"
                >
                  <option value="nonfoil">Non-Foil Preferred</option>
                  <option value="foil">Foil Preferred</option>
                  <option value="any">Any Cheapest Finish</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase text-white/50 mb-1">Printing</label>
                <select
                  value={printingPreference}
                  onChange={(e) => setPrintingPreference(e.target.value as PrintingPreference)}
                  className="w-full rounded-lg bg-slate-950 border border-white/15 px-2.5 py-1.5 text-white"
                >
                  <option value="cheapest_printing">Cheapest Any Printing</option>
                  <option value="exact_printing">Match Set Code First</option>
                </select>
              </div>

              <div className="flex flex-col justify-end gap-1.5">
                <label className="inline-flex items-center gap-2 cursor-pointer text-white/80">
                  <input
                    type="checkbox"
                    checked={excludeBasicLands}
                    onChange={(e) => setExcludeBasicLands(e.target.checked)}
                    className="rounded border-white/20"
                  />
                  <span>Exclude Basic Lands</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowOwnedBox((v) => !v)}
                  className="text-left text-[11px] text-cyan-300 hover:underline"
                >
                  {showOwnedBox ? 'Hide Paste/CSV Owned List' : '+ Paste CSV / Extra Owned Cards'}
                </button>
              </div>
            </div>

            {showOwnedBox && (
              <div>
                <label className="block text-[10px] uppercase text-white/50 mb-1">
                  Subtract Extra Owned Cards (Plaintext `4 Sol Ring` or Moxfield / Archidekt CSV)
                </label>
                <textarea
                  rows={3}
                  value={extraOwnedText}
                  onChange={(e) => setExtraOwnedText(e.target.value)}
                  placeholder={'1 Sol Ring\n1 Command Tower\n1 Arcane Signet'}
                  className="w-full rounded-lg bg-slate-950 border border-white/15 p-2 font-mono text-xs text-white"
                />
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase text-white/50 mb-1.5">Allowed Marketplaces</div>
              <div className="flex flex-wrap gap-1.5">
                {ALL_VENDOR_IDS.map((vid) => {
                  const sched = VENDOR_SHIPPING_SCHEDULES[vid];
                  const active = allowedVendors.includes(vid);
                  return (
                    <button
                      key={vid}
                      type="button"
                      onClick={() => toggleVendor(vid)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                        active
                          ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-200'
                          : 'bg-white/5 border-white/10 text-white/40'
                      }`}
                    >
                      {sched.vendorLabel}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ========== OUT-OF-STOCK RE-OPTIMIZATION BANNER ========== */}
      {lastReoptReport && (
        <div className="rounded-xl bg-amber-950/60 border border-amber-500/40 p-3 text-xs flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-0.5">
            <div className="font-bold text-amber-200 flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-emerald-400" />
              <span>
                Cart Re-Optimized Around Out-of-Stock Listings ({fmtMoney(lastReoptReport.previousGrandTotalUsd)} →{' '}
                {fmtMoney(lastReoptReport.newGrandTotalUsd)})
              </span>
            </div>
            {lastReoptReport.affectedCards.length > 0 && (
              <div className="text-amber-100/80 text-[11px]">
                Re-routed:{' '}
                {lastReoptReport.affectedCards
                  .map(
                    (c) =>
                      `${c.cardName} (${c.previousSellerName} → ${c.newSellerNames.join(', ') || 'unfulfilled'})`
                  )
                  .join(' · ')}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setStockUpdates([]);
              setLastReoptReport(null);
            }}
            className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-[11px]"
          >
            Reset Stock Overrides
          </button>
        </div>
      )}

      {/* ========== UNFULFILLED ITEMS WARNING ========== */}
      {optimized.unfulfilled.length > 0 && (
        <div className="rounded-xl bg-rose-950/50 border border-rose-500/40 p-3 text-xs text-rose-200 flex items-start gap-2">
          <AlertCircle className="size-4 text-rose-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Some cards had insufficient stock under current filters:</div>
            <div className="text-[11px] text-rose-200/80 mt-0.5">
              {optimized.unfulfilled.map((u) => `${u.missingQty}× ${u.cardName}`).join(', ')}
            </div>
          </div>
        </div>
      )}

      {/* ========== PACKAGE PLANS ========== */}
      <div className="space-y-3">
        {optimized.packages.map((pkg, idx) => {
          const isTcg = pkg.vendorId === 'tcgplayer_direct' || pkg.vendorId === 'tcgplayer_market';
          const checkoutHref = isTcg
            ? buildTCGPlayerCartUrl(
                pkg.items.map((i) => ({
                  name: i.cardName,
                  quantity: i.quantity,
                  priceUsd: i.unitPriceUsd,
                }))
              )
            : pkg.vendorId === 'card_kingdom' && pkg.items.length === 1
            ? buildCardKingdomUrl(pkg.items[0].cardName)
            : pkg.vendorId === 'cardmarket' && pkg.items.length === 1
            ? buildCardmarketUrl(pkg.items[0].cardName)
            : pkg.checkoutUrl;

          const checklistOpen = Boolean(expandedChecklists[pkg.sellerId]);

          return (
            <div
              key={pkg.sellerId}
              className="rounded-xl bg-slate-950/80 border border-white/10 overflow-hidden shadow-lg"
            >
              <div className="p-3.5 bg-white/[0.03] border-b border-white/10 flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 font-bold text-[10px]">
                      Package #{idx + 1}
                    </span>
                    <span className="font-bold text-sm text-white">{pkg.sellerName}</span>
                    <span className="text-[11px] text-white/40">({pkg.vendorLabel})</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
                    <span>{pkg.totalCardQty} cards</span>
                    <span>·</span>
                    <span>Subtotal {fmtMoney(pkg.subtotalUsd)}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Truck className="size-3 text-cyan-400" />
                      {pkg.freeShippingUnlocked ? (
                        <span className="text-emerald-400 font-semibold">FREE Shipping Unlocked</span>
                      ) : (
                        <span>Shipping {fmtMoney(pkg.shippingUsd)}</span>
                      )}
                    </span>
                    {!pkg.freeShippingUnlocked && pkg.amountToFreeShippingUsd !== null && (
                      <span className="text-[11px] text-amber-300/80">
                        ({fmtMoney(pkg.amountToFreeShippingUsd)} from free shipping)
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-right mr-1">
                    <div className="text-[10px] uppercase text-white/40">Package Total</div>
                    <div className="text-base font-black text-white tabular-nums">{fmtMoney(pkg.totalUsd)}</div>
                  </div>

                  <button
                    type="button"
                    onClick={() => void copyPackageList(pkg.sellerId, pkg.massEntryText)}
                    className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 text-xs font-semibold text-slate-100 flex items-center gap-1.5"
                  >
                    {copiedPkgId === pkg.sellerId ? (
                      <>
                        <Check className="size-3.5 text-emerald-400" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="size-3.5" />
                        <span>Copy Mass Entry</span>
                      </>
                    )}
                  </button>

                  <a
                    href={checkoutHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3.5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow"
                  >
                    <Package className="size-3.5" />
                    <span>Checkout</span>
                    <ExternalLink className="size-3" />
                  </a>
                </div>
              </div>

              {/* Package Line Items */}
              <div className="divide-y divide-white/[0.04] max-h-64 overflow-y-auto">
                {pkg.items.map((line) => (
                  <div
                    key={line.quoteId}
                    className="px-3.5 py-1.5 flex items-center justify-between gap-2 text-xs hover:bg-white/[0.02]"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-white/50 w-6 text-right tabular-nums">
                        {line.quantity}×
                      </span>
                      <span className="text-white/90 font-medium truncate">{line.cardName}</span>
                      <span className="px-1.5 py-0.5 rounded bg-slate-900 border border-slate-800 text-[10px] font-mono text-slate-400 uppercase">
                        {line.setCode} · {line.condition}
                        {line.foil ? ' · FOIL' : ''}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[11px] text-white/45 tabular-nums">
                        {fmtMoney(line.unitPriceUsd)} ea
                      </span>
                      <span className="font-semibold text-amber-300 w-16 text-right tabular-nums">
                        {fmtMoney(line.lineTotalUsd)}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleMarkLineSoldOut(line.quoteId)}
                        title="Sold out at checkout? Click to re-route this card across remaining sellers"
                        className="px-1.5 py-0.5 rounded bg-rose-950/60 hover:bg-rose-900 border border-rose-500/30 text-[10px] text-rose-200"
                      >
                        OOS
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Collapsible Guided Checkout Checklist */}
              <div className="px-3.5 py-2 bg-slate-950 border-t border-white/5">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedChecklists((prev) => ({
                      ...prev,
                      [pkg.sellerId]: !prev[pkg.sellerId],
                    }))
                  }
                  className="text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1 font-medium"
                >
                  {checklistOpen ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                  <span>Guided 4-Step Mass-Entry Checkout Checklist</span>
                </button>
                {checklistOpen && (
                  <ol className="mt-2 space-y-1 pl-4 list-decimal text-[11px] text-slate-300">
                    {pkg.stepChecklist.map((step, sIdx) => (
                      <li key={sIdx}>{step}</li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
